import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ResidentsIndexResponse } from "app/services/api/types/residents/responses/ResidentsIndexResponse";
import { RootState } from "app/services/store";
import { PaginatedResponse } from "app/services/api/types/common/responses/PaginatedResponse";
import { ResidentPersistedModel } from "app/services/api/types/residents/models/ResidentPersistedModel";

interface ResidentsSliceState {
  residentsPrefetched: boolean;
  residentIds: number[];
  residents: Record<number, PaginatedResponse<ResidentPersistedModel>>;
}

const initialState: ResidentsSliceState = {
  residentsPrefetched: false,
  residentIds: [],
  residents: {},
};

export const residentsSlice = createSlice({
  name: "residents",
  initialState,
  reducers: {
    setResidentsPrefetched: (state, action: PayloadAction<boolean>) => ({
      ...state,
      residentsPrefetched: action.payload,
    }),
    setResidentsIndexResponse: (
      state,
      action: PayloadAction<ResidentsIndexResponse>,
    ) => {
      // Convert existing IDs and new IDs to Set for uniqueness
      const uniqueIds = new Set([
        ...state.residentIds,
        ...action.payload.data.map((resident) => resident.id),
      ]);

      // Map to ResidentPersistedModel to reduce memory usage
      const mappedData: ResidentPersistedModel[] = action.payload.data.map(
        (resident) => ({
          id: resident.id,
          resident_id: resident.resident_id,
          first_name: resident.first_name,
          last_name: resident.last_name,
          known_as: resident.known_as,
          profile_picture: resident.profile_picture,
          room: resident.room,
          bed: resident.bed,
          status: resident.status,
          dependency: resident.dependency,
          status_flags: resident.status_flags,
          admission_start_date: resident.admission_start_date,
          gender: resident.gender,
          location: resident.location,
        }),
      );

      const mappedPayload: PaginatedResponse<ResidentPersistedModel> = {
        ...action.payload,
        data: mappedData,
      };

      // Convert Set back to array
      return {
        ...state,
        residentIds: Array.from(uniqueIds),
        residents: {
          ...state.residents,
          [action.payload.meta!.current_page]: mappedPayload,
        },
      };
    },
  },
});

// Action creators are generated for each case reducer function
export const { setResidentsIndexResponse, setResidentsPrefetched } =
  residentsSlice.actions;

export const selectResident = createSelector(
  [
    (state: RootState) =>
      state.residents ? Object.values(state.residents.residents) : [],
    (_: RootState, residentId: number) => residentId,
  ],
  (residents, residentId) => {
    const findResident = (
      residentsIndexResponse: PaginatedResponse<ResidentPersistedModel>,
    ) => residentsIndexResponse.data.find((item) => item.id === residentId);
    for (let i = 0; i < residents.length; i++) {
      const residentIndexResponse = residents[i];
      const foundResident =
        residentIndexResponse && findResident(residentIndexResponse);
      if (foundResident) {
        return foundResident;
      }
    }
  },
);
export default residentsSlice.reducer;
